import { useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/Button";
import { Modal, ModalTitle } from "@/components/Modal";
import { Select } from "@/components/Select";
import type { ToastVariant } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { plural } from "@/lib/plural";

export function InviteModal({
  showToast,
  onClose,
  onSent,
}: {
  showToast: (message: string, variant: ToastVariant) => void;
  onClose: () => void;
  onSent: () => void;
}) {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const [emails, setEmails] = useState("");
  const [role, setRole] = useState("User");
  const [submitting, setSubmitting] = useState(false);

  async function sendInvite(email: string) {
    try {
      await api.send(
        `/organizations/${orgId}/invites`,
        "POST",
        { email, role },
        "failed to send",
      );
    } catch (err) {
      throw new Error(
        `${email}: ${err instanceof Error ? err.message : "failed to send"}`,
      );
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!accessToken || !orgId) {
      return;
    }

    const addresses = [
      ...new Set(
        emails
          .split(",")
          .map((email) => email.trim())
          .filter(Boolean),
      ),
    ];

    if (addresses.length === 0) {
      return;
    }

    setSubmitting(true);

    const results = await Promise.allSettled(addresses.map(sendInvite));
    const failures = results.filter(
      (r): r is PromiseRejectedResult => r.status === "rejected",
    );
    const succeededCount = addresses.length - failures.length;

    if (failures.length === 0) {
      showToast(
        succeededCount === 1
          ? `Invite sent to ${addresses[0]}.`
          : `${succeededCount} invites sent.`,
        "success",
      );
      setEmails("");
      onSent();
      onClose();
    } else {
      const failureDetails = failures
        .map((f) =>
          f.reason instanceof Error ? f.reason.message : "failed to send",
        )
        .join(", ");

      showToast(
        succeededCount === 0
          ? `Couldn't send ${plural(addresses.length, "invite")}: ${failureDetails}`
          : `${succeededCount} of ${plural(addresses.length, "invite")} sent. The rest failed: ${failureDetails}`,
        "error",
      );
      onSent();
    }

    setSubmitting(false);
  }

  return (
    <Modal onClose={onClose} scrollable={false}>
      <ModalTitle className="mb-4 shrink-0">Invite users</ModalTitle>
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div>
            <label
              htmlFor="inviteEmails"
              className="mb-1.5 block text-sm text-foreground"
            >
              Emails
            </label>
            <textarea
              id="inviteEmails"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="fellow-adventurer@example.com, traveling-companion@example.com"
              required
              autoFocus
              rows={3}
              className="w-full rounded-lg border border-muted bg-surface px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </div>
          <div>
            <label
              htmlFor="inviteRole"
              className="mb-1.5 block text-sm text-foreground"
            >
              Role
            </label>
            <Select
              id="inviteRole"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full"
            >
              <option value="User">User</option>
              <option value="Admin">Admin</option>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex shrink-0 justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? "Sending..." : "Send invites"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
